import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { HomeSpotlightFacade } from '../../../../../Facades/home-spotlight.facade';
import { HomeSpotlightEntry } from '../../../Interfaces/home-spotlight-entry.interface';
import { HomeSpotlightGroup } from '../../../Interfaces/home-spotlight-group.interface';
import { HOME_SPOTLIGHT_PANEL_ID } from '../../../Consts/home-spotlight-panel.const';

@Component({
  selector: 'app-home-spotlight',
  standalone: true,
  imports: [COMMON_IMPORTS],
  templateUrl: './home-spotlight.component.html',
  styleUrl: './home-spotlight.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
  },
})
export class HomeSpotlightComponent {
  readonly spotlight = inject(HomeSpotlightFacade);

  private readonly _router = inject(Router);
  private readonly _injector = inject(Injector);
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly _field = viewChild.required<ElementRef<HTMLInputElement>>('field');

  private readonly _open = signal(false);
  private readonly _highlighted = signal(0);

  readonly panelId = HOME_SPOTLIGHT_PANEL_ID;

  readonly groups = computed<HomeSpotlightGroup[]>(() =>
    this.spotlight.hasQuery() ? this.spotlight.groups() : this.spotlight.suggestions()
  );

  readonly flatEntries = computed<HomeSpotlightEntry[]>(() =>
    this.groups().flatMap(group => group.entries)
  );

  readonly highlightedKey = computed(() => this.flatEntries()[this.boundedIndex()]?.key ?? '');

  readonly isPanelOpen = computed(
    () =>
      this._open() &&
      (this.flatEntries().length > 0 ||
        this.spotlight.isEmpty() ||
        this.spotlight.isSearching() ||
        this.spotlight.isTyping())
  );

  readonly shortcutLabelKey = computed(() =>
    this.isApplePlatform() ? 'app.home.spotlight.shortcut_apple' : 'app.home.spotlight.shortcut'
  );

  optionId(entry: HomeSpotlightEntry): string {
    return `${HOME_SPOTLIGHT_PANEL_ID}-${entry.kind}-${entry.key}`;
  }

  onInput(value: string): void {
    this.spotlight.setQuery(value);
    this._highlighted.set(0);
    this._open.set(true);
  }

  onFocus(): void {
    this._open.set(true);
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.isShortcut(event)) {
      event.preventDefault();
      this.focusField();
      return;
    }

    if (!this._open() || !this.isFieldFocused()) return;

    if (event.key === 'Escape') {
      this.dismiss();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.move(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.openHighlighted();
    }
  }

  onDocumentPointerDown(event: Event): void {
    const target = event.target;
    if (target instanceof Node && this._host.nativeElement.contains(target)) return;
    this._open.set(false);
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget;
    if (next instanceof Node && this._host.nativeElement.contains(next)) return;
    this._open.set(false);
  }

  select(): void {
    this.spotlight.clear();
    this._open.set(false);
  }

  clear(): void {
    this.spotlight.clear();
    this._highlighted.set(0);
    this.focusField();
  }

  private boundedIndex(): number {
    const total = this.flatEntries().length;
    if (total === 0) return 0;
    return Math.min(this._highlighted(), total - 1);
  }

  private move(delta: number): void {
    const total = this.flatEntries().length;
    if (total === 0) return;
    this._highlighted.set((this.boundedIndex() + delta + total) % total);
    this.revealHighlighted();
  }

  private revealHighlighted(): void {
    afterNextRender(
      () =>
        this._host.nativeElement
          .querySelector('.panel-entry.is-highlighted')
          ?.scrollIntoView({ block: 'nearest' }),
      { injector: this._injector }
    );
  }

  private openHighlighted(): void {
    const entry = this.flatEntries()[this.boundedIndex()];
    if (entry === undefined) return;
    this.spotlight.clear();
    this._open.set(false);
    this._field().nativeElement.blur();
    this._router.navigate([...entry.routerLink]);
  }

  private dismiss(): void {
    if (this.spotlight.hasQuery()) {
      this.spotlight.clear();
      return;
    }
    this._open.set(false);
    this._field().nativeElement.blur();
  }

  private focusField(): void {
    this._open.set(true);
    this._field().nativeElement.focus();
    this._field().nativeElement.select();
  }

  private isFieldFocused(): boolean {
    return document.activeElement === this._field().nativeElement;
  }

  private isShortcut(event: KeyboardEvent): boolean {
    return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
  }

  private isApplePlatform(): boolean {
    return /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);
  }
}
